import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowLeft,
	faArrowRight,
	faChampagneGlasses,
	faCloudCheck,
	faCloudPlus,
	faMars,
	faTransgender,
	faTrash,
	faVenus,
} from '@fortawesome/pro-duotone-svg-icons';

import {
	Button,
	Checkbox,
	Divider,
	Form,
	Input,
	Radio,
	Switch,
	Tooltip,
	Typography,
	notification,
} from 'antd';

import { txt } from '../../../locales/i18n';
import { DeleteModal } from '../../shared/delete-modal';
import * as AdvertisementService from '../../../services/bb-api/advertisement';
import * as MunicipalityService from '../../../services/bb-api/municipality';
import * as Municipalities from '../../../context/municipalities';
import { useDeviceSize } from '../../../hooks/device-size';

type FormProps = React.ComponentProps<typeof Form>;

type Advertisement = {
	id: number;
	image: string;
	linkTracking: string;
	linkProduct: string;
};

const newAdvertisementSymbol = '$';
const requiredRule = { required: true, message: txt('global.required') };

const flags = {
	dk: 'https://filer.boblberg.dk/Assets/Img/flags/da.png',
	se: 'https://filer.boblberg.dk/Assets/Img/flags/sv.png',
	de: 'https://filer.boblberg.dk/Assets/Img/flags/de.png',
} as const;

const imageSizes = {
	small: { height: 100, width: 400 },
	large: { height: 100, width: 800 },
} as const;

export const AdvertisementPage = () => {
	const [form] = Form.useForm();
	const [isLoading, setIsLoading] = useState(true);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [inactiveMunicipalities, setInactiveMunicipalities] = useState([]);
	const firstContentContainerRef = useRef<HTMLDivElement>(null);
	const [municipalitySearch, setMunicipalitySearch] = useState('');
	const [active, setActive] = useState(false);
	const isMobileDevice = useDeviceSize();

	const history = useHistory();
	const { advertisementId } = useParams<{ advertisementId: string }>();

	useEffect(() => {
		if (advertisementId === newAdvertisementSymbol) {
			const getInactiveMunicipalities = async () => {
				const query = { active: false, country: 'Denmark' };
				const inactiveMunicipalities = await MunicipalityService.query(query);

				setInactiveMunicipalities(inactiveMunicipalities);
				return setIsLoading(false);
			};

			getInactiveMunicipalities();
			return;
		}

		const getAdvertisement = async () => {
			try {
				const advertisement = await AdvertisementService.get(Number(advertisementId));
				form.setFieldsValue(advertisement);
				setActive(advertisement.active);

				const query = { active: false, country: advertisement.region };
				const inactiveMunicipalities = await MunicipalityService.query(query);
				setInactiveMunicipalities(inactiveMunicipalities);
			} catch (error) {
				const message = txt('global.not found');
				notification.error({ message });
				history.goBack();
			}

			setIsLoading(false);
		};

		getAdvertisement();
	}, [advertisementId, history, form]);

	useEffect(() => {
		document.onkeydown = async (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 's') {
				event.preventDefault();
				form.submit();
			}
		};

		return () => {
			document.onkeydown = () => null;
		};
	}, [form]);

	useEffect(() => {
		form.setFields([{ name: 'active', value: active }]);
	}, [form, active]);

	const submit = async (advertisement: Advertisement) => {
		const actions = { create: createAdvertisement, update: updateAdvertisement };
		const action = advertisementId === newAdvertisementSymbol ? actions.create : actions.update;

		setIsLoading(true);
		await action(advertisement);
		setIsLoading(false);
	};

	const createAdvertisement = async (advertisement: Advertisement) => {
		try {
			const advertisementId = await AdvertisementService.create(advertisement);
			const message = txt('notification.create successful');
			notification.success({ message });
			return history.push('/advertisements/' + advertisementId);
		} catch (error) {
			const message = txt('notification.create failed');
			notification.error({ message });
		}
	};

	const updateAdvertisement = async (advertisement: Advertisement) => {
		try {
			await AdvertisementService.update(Number(advertisementId), advertisement);
			const message = txt('notification.update successful');
			notification.success({ message });
			return history.push('/advertisements');
		} catch (error) {
			const message = txt('notification.update failed');
			notification.error({ message });
		}
	};

	const deleteAdvertisement = async () => {
		await AdvertisementService.remove(Number(advertisementId));
		history.replace('/advertisements');
	};

	const getFormHeight = () => {
		const firstContentContainer = firstContentContainerRef.current;
		if (firstContentContainer === null) return;
		const height = firstContentContainer.offsetHeight;
		return height;
	};

	const handleChange: FormProps['onValuesChange'] = async (value) => {
		if ('region' in value) {
			setIsLoading(true);

			try {
				const query = { active: false, country: value.region };
				const inactiveMunicipalities = await MunicipalityService.query(query);
				setInactiveMunicipalities(inactiveMunicipalities);
			} catch (error) {
				console.error(error);
			}

			setIsLoading(false);
		}
	};

	const selectMunicipalities = (type: string) => {
		const municipalityIds = inactiveMunicipalities.map(({ id }) => id as number);

		if (type === 'all') {
			return form.setFields([{ name: 'municipalities', value: municipalityIds }]);
		}

		if (type === 'none') {
			return form.setFields([{ name: 'municipalities', value: [] }]);
		}

		const region = Municipalities.regions[type] ?? null;
		if (region === null) return;

		const regionFiltered = region.filter((id) => {
			return municipalityIds.includes(id)
		})

		const currentSelections = form.getFieldValue('municipalities');
		const value = [...currentSelections, ...regionFiltered];

		return form.setFields([{ name: 'municipalities', value }]);
	};

	const imageValidator = (image: EventTarget & HTMLImageElement, sizeKey: 'small' | 'large') => {
		const height = image.naturalHeight ?? null;
		const width = image.naturalWidth ?? null;
		const size = imageSizes[sizeKey];

		if (width / height !== size.width / size.height) {
			const message = txt('global.image ratio warning');
			notification.warn({ message });
		}
	};

	return (
		<AdvertisementPageStyled>
			<section className="header-container">
				<Tooltip placement="bottom" title={txt('global.active')}>
					<Switch
						loading={isLoading}
						onChange={setActive}
						checked={active}
						size={isMobileDevice ? 'small' : 'default'}
					/>
				</Tooltip>

				<span className="buttons-container">
					{advertisementId === newAdvertisementSymbol && (
						<Tooltip placement="bottom" title={txt('global.cancel')}>
							<Button
								type="link"
								loading={isLoading}
								onClick={history.goBack}
								size={isMobileDevice ? 'small' : 'middle'}
							>
								<FontAwesomeIcon icon={faArrowLeft} />
							</Button>
						</Tooltip>
					)}

					<Tooltip
						placement="bottom"
						title={
							advertisementId === newAdvertisementSymbol
								? txt('global.create')
								: txt('global.update')
						}
					>
						<Button
							type="link"
							onClick={form.submit}
							loading={isLoading}
							size={isMobileDevice ? 'small' : 'middle'}
						>
							<FontAwesomeIcon
								icon={
									advertisementId === newAdvertisementSymbol
										? faCloudPlus
										: faCloudCheck
								}
							/>
						</Button>
					</Tooltip>
				</span>

				{advertisementId !== newAdvertisementSymbol && (
					<Tooltip placement="bottom" title={txt('global.delete')}>
						<Button
							danger
							type="text"
							loading={isLoading}
							onClick={() => setShowDeleteModal(true)}
						>
							<FontAwesomeIcon icon={faTrash} />
						</Button>
					</Tooltip>
				)}
			</section>

			<div className="wrapper">
				<Form
					form={form}
					layout="vertical"
					onFinish={submit}
					requiredMark={false}
					onValuesChange={handleChange}
				>
					<section>
						<h1>{txt('global.advertisement')}</h1>

						<div className="content-container" ref={firstContentContainerRef}>
							<div className="flex-row">
								<div className="row-item" style={{ flex: 1 }}>
									<label>{txt('global.advertiser')}</label>

									<Form.Item noStyle name="advertiser" rules={[requiredRule]}>
										<Input placeholder={txt('global.advertiser')} />
									</Form.Item>
								</div>

								<div className="row-item">
									<label>{txt('global.ad label')}</label>

									<span>
										<Form.Item
											noStyle
											name="adLabel"
											valuePropName="checked"
											initialValue={false}
										>
											<Switch />
										</Form.Item>
									</span>
								</div>
							</div>

							<Form.Item
								name="active"
								className="hide"
								valuePropName="checked"
								initialValue={false}
							>
								<Switch />
							</Form.Item>

							<div className="image-container">
								<label>
									{txt('global.image')} {imageSizes.large.height}x
									{imageSizes.large.width} (Desktop)
								</label>

								<Form.Item noStyle shouldUpdate>
									{() => (
										<img
											className="large"
											alt={txt('global.image link')}
											src={form.getFieldValue('imageLarge')}
											onLoad={({ currentTarget }) =>
												imageValidator(currentTarget, 'large')
											}
										/>
									)}
								</Form.Item>

								<Form.Item noStyle name="imageLarge" rules={[requiredRule]}>
									<Input allowClear placeholder={txt('global.image link')} />
								</Form.Item>
							</div>

							<div className="image-container">
								<label>
									{txt('global.image')} {imageSizes.small.height}x
									{imageSizes.small.width} (Mobile)
								</label>

								<Form.Item noStyle shouldUpdate>
									{() => (
										<img
											className="small"
											alt={txt('global.image link')}
											src={form.getFieldValue('imageSmall')}
											onLoad={({ currentTarget }) =>
												imageValidator(currentTarget, 'small')
											}
										/>
									)}
								</Form.Item>

								<Form.Item noStyle name="imageSmall" rules={[requiredRule]}>
									<Input allowClear placeholder={txt('global.image link')} />
								</Form.Item>
							</div>

							<div>
								<label>{txt('global.teaser')}</label>

								<Form.Item name="teaser" initialValue="">
									<Input.TextArea placeholder={txt('global.teaser')} />
								</Form.Item>
							</div>

							<div>
								<label>{txt('global.tracking link')}</label>

								<div className="link-container">
									<Form.Item name="linkTracking" rules={[requiredRule]}>
										<Input placeholder={txt('global.tracking link')} />
									</Form.Item>

									<Form.Item noStyle shouldUpdate>
										{() => (
											<Button
												type="link"
												target="_blank"
												href={form.getFieldValue('linkTracking')}
											>
												<FontAwesomeIcon icon={faArrowRight} />
											</Button>
										)}
									</Form.Item>
								</div>
							</div>

							<div>
								<label>{txt('global.product link')}</label>

								<div className="link-container">
									<Form.Item name="linkProduct" rules={[requiredRule]}>
										<Input placeholder={txt('global.product link')} />
									</Form.Item>

									<Form.Item noStyle shouldUpdate>
										{() => (
											<Button
												type="link"
												target="_blank"
												href={form.getFieldValue('linkProduct')}
											>
												<FontAwesomeIcon icon={faArrowRight} />
											</Button>
										)}
									</Form.Item>
								</div>
							</div>
						</div>
					</section>

					<section>
						<h1>{txt('global.exposure')}</h1>

						<div className="content-container" style={{ height: getFormHeight() }}>
							<div className="row-item">
								<label>{txt('global.gender')}</label>

								<Form.Item noStyle name="genders" initialValue={[1, 2, 3]}>
									<Checkbox.Group>
										<Checkbox value={1}>
											{isMobileDevice === false && (
												<span>{txt('genders.male')} </span>
											)}

											<FontAwesomeIcon icon={faMars} />
										</Checkbox>

										<Checkbox value={2}>
											{isMobileDevice === false && (
												<span>{txt('genders.female')} </span>
											)}

											<FontAwesomeIcon icon={faVenus} />
										</Checkbox>

										<Checkbox value={3}>
											{isMobileDevice === false && (
												<span>{txt('genders.other')} </span>
											)}

											<FontAwesomeIcon icon={faTransgender} />
										</Checkbox>
									</Checkbox.Group>
								</Form.Item>
							</div>

							<div className="flex-row" style={{ justifyContent: 'space-between' }}>
								<div className="row-item">
									<label>{txt('global.country')}</label>

									<Form.Item noStyle name="region" initialValue="Denmark">
										<Radio.Group>
											<Radio value="Denmark">
												<img
													className="region-flag"
													src={flags.dk}
													alt="dk flag"
												/>
											</Radio>

											<Radio value="Sweden">
												<img
													className="region-flag"
													src={flags.se}
													alt="se flag"
												/>
											</Radio>

											<Radio value="Germany">
												<img
													className="region-flag"
													src={flags.de}
													alt="de flag"
												/>
											</Radio>
										</Radio.Group>
									</Form.Item>
								</div>

								<div className="row-item">
									<label>Nytårsvener</label>

									<div>
										<Form.Item
											noStyle
											name="newYear"
											valuePropName="checked"
											initialValue={false}
										>
											<Switch
												checkedChildren={
													<FontAwesomeIcon icon={faChampagneGlasses} />
												}
												unCheckedChildren={
													<FontAwesomeIcon icon={faChampagneGlasses} />
												}
											/>
										</Form.Item>
									</div>
								</div>
							</div>

							<Input
								allowClear
								placeholder={txt('global.search')}
								onChange={({ target }) =>
									setMunicipalitySearch(target.value.toLowerCase())
								}
							/>

							<Form.Item noStyle name="municipalities" label="Checkbox.Group">
								<Checkbox.Group>
									{inactiveMunicipalities.map(
										({ name, id }: { name: string; id: number }) => {
											const classNames = [];
											const nameNormalized = name.toLowerCase();

											const searchMatch =
												nameNormalized.startsWith(municipalitySearch);

											if (searchMatch === false) classNames.push('hide');

											return (
												<Checkbox
													key={id}
													value={id}
													className={classNames.join(' ')}
												>
													<Tooltip title={name}>
														<Typography.Text ellipsis>
															{name}
														</Typography.Text>
													</Tooltip>
												</Checkbox>
											);
										}
									)}
								</Checkbox.Group>
							</Form.Item>

							<Divider />

							<div className="municipality-buttons-container">
								<div className="flex-row">
									<Button
										danger
										size="small"
										type="link"
										onClick={() => selectMunicipalities('none')}
									>
										{txt('municipality-buttons.none')}
									</Button>

									<Button
										size="small"
										type="link"
										onClick={() => selectMunicipalities('all')}
									>
										{txt('municipality-buttons.all')}
									</Button>
								</div>

								<div className="municipality-buttons-container">
									<Form.Item noStyle shouldUpdate>
										{() => {
											const region = form.getFieldValue('region');
											if (region !== 'Denmark') return null;

											return (
												<>
													<Button
														size="small"
														type="link"
														onClick={() => selectMunicipalities('Fyn')}
													>
														{txt('municipality-buttons.fyn')}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Nordjylland')
														}
													>
														{txt(
															'municipality-buttons.north-jytland-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Midt-Vestjylland')
														}
													>
														{txt(
															'municipality-buttons.mid-west-jytland-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Midt-Østjylland')
														}
													>
														{txt(
															'municipality-buttons.mid-east-jytland-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Sønderjylland')
														}
													>
														{txt(
															'municipality-buttons.south-jytland-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Storkøbenhavn')
														}
													>
														{txt(
															'municipality-buttons.copenhagen-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Vestegnen')
														}
													>
														{txt(
															'municipality-buttons.west-copenhagen'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Nordsjælland')
														}
													>
														{txt(
															'municipality-buttons.north-zealand-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Vestsjælland')
														}
													>
														{txt(
															'municipality-buttons.west-zealand-short'
														)}
													</Button>

													<Button
														size="small"
														type="link"
														onClick={() =>
															selectMunicipalities('Sydøstsjælland')
														}
													>
														{txt(
															'municipality-buttons.south-east-zealand-short'
														)}
													</Button>
												</>
											);
										}}
									</Form.Item>
								</div>
							</div>
						</div>
					</section>
				</Form>
			</div>

			<DeleteModal
				onConfirm={deleteAdvertisement}
				useVisible={[showDeleteModal, setShowDeleteModal]}
			/>
		</AdvertisementPageStyled>
	);
};

const AdvertisementPageStyled = styled.main`
	display: flex;
	flex-direction: column;
	background-color: ghostwhite;

	.header-container {
		position: sticky;
		top: 0;
		z-index: 1;

		display: flex;
		align-items: center;
		gap: 1rem;

		background-color: white;
		box-shadow: 0 6px 6px ghostwhite;
		padding: 0.6rem 1rem;

		.buttons-container {
			margin-left: auto;
		}
	}

	.hide {
		display: none;
	}

	form {
		display: flex;
		flex-wrap: wrap;
		gap: 2rem;

		> section:first-child {
			flex: 3;
		}

		> section:last-child {
			flex: 2;
		}
	}

	h1,
	label {
		color: #4f6179;
	}

	label {
		font-weight: bold;
	}

	.content-container {
		padding: 2rem;
		border-radius: 1rem;
		background-color: white;
		box-shadow: 0 0 1rem #99c3;
		height: fit-content;
		min-width: 320px;

		display: flex;
		flex-direction: column;
		box-sizing: border-box;
	}

	.wrapper {
		width: 100%;
		max-width: 1090px;
		padding: 2rem 1rem;
		align-self: center;
	}

	.flex-row {
		display: flex;
		gap: 0 1rem;
		flex-wrap: wrap;
	}

	.image-container {
		display: flex;
		flex-direction: column;
		margin-bottom: 24px;

		img {
			background-color: ghostwhite;
			flex: 1;
			width: 100%;
			object-fit: contain;
			overflow: hidden;

			&.large {
				aspect-ratio: ${imageSizes.large.width} / ${imageSizes.large.height};
			}

			&.small {
				aspect-ratio: ${imageSizes.small.width} / ${imageSizes.small.height};
			}
		}
	}

	.link-container {
		display: flex;
		width: 100%;

		.ant-form-item {
			flex: 1;
		}
	}

	#municipalities {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(92px, 1fr));
		gap: 1ch 2ch;

		margin-top: 1rem;
		overflow-y: auto;
		flex: 1;

		.ant-checkbox-wrapper,
		.ant-typography {
			margin: 0;
			font-weight: normal;
		}

		.ant-checkbox-wrapper {
			span:last-child {
				overflow-x: hidden;
			}
		}
	}

	.row-item {
		display: flex;
		flex-direction: column;
		margin-bottom: 24px;
	}

	.region-flag {
		width: 2rem;
	}

	.municipality-buttons-container {
		display: flex;
		flex-wrap: wrap;
		gap: 0.2rem 1rem;
		justify-content: center;
	}
`;
